import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuoteTemplate } from '../../../hooks/useTemplateSettings';
import { useCompanyStore } from '../../../stores/companyStore';
import { QuoteData } from '../types';
import { QuoteTemplateClassic } from './QuoteTemplateClassic';
import { QuoteTemplateModern } from './QuoteTemplateModern';
import { QuoteTemplateCorporate } from './QuoteTemplateCorporate';
import { QuoteTemplateElegant } from './QuoteTemplateElegant';
import { logger } from '../../../utils/logger';

interface QuoteTemplateRouterProps {
  quoteData: QuoteData;
  onClose: () => void;
}

/**
 * Router de templates de devis
 * Sélectionne le bon template selon le nom du template en base de données
 */
export const QuoteTemplateRouter: React.FC<QuoteTemplateRouterProps> = ({
  quoteData,
  onClose
}) => {
  const { t } = useTranslation();
  const { template, loading, error } = useQuoteTemplate();
  const { company } = useCompanyStore();

  // Chargement
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
            <p className="text-gray-700">{t('templates.common.loadingTemplate')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Erreur
  if (error) {
    logger.error('Erreur chargement template:', error);
    // Fallback sur template classique en cas d'erreur (sans couleur custom)
    return (
      <QuoteTemplateClassic
        quoteData={quoteData}
        onClose={onClose}
        template={template || undefined}
        company={company}
        color={'#0D9488'}
      />
    );
  }

  // Sélectionner le template selon le displayName (pas le name technique)
  // Mapping: displayName (Classic, Modern, Corporate, Elegant) → lowercase
  const templateName = template?.displayName?.toLowerCase() || 'classic';

  // ✅ LOGIC: Utiliser les couleurs de la société SI useCompanyColors = true
  const companyColors = (template as any)?.companyColors;
  const shouldUseCompanyColors = companyColors?.useCompanyColors === true;
  const primaryColor = shouldUseCompanyColors && companyColors?.colorPrimary
    ? companyColors.colorPrimary
    : template?.styleConfig?.primaryColor;

  logger.log('Résolution template devis:', {
    dbName: template?.name,
    displayName: template?.displayName,
    templateName,
    hasStyleConfig: !!template?.styleConfig,
    useCompanyColors: shouldUseCompanyColors,
    colorPrimary: primaryColor,
  });

  switch (templateName) {
    case 'classic':
      return (
        <QuoteTemplateClassic
          quoteData={quoteData}
          onClose={onClose}
          template={template || undefined}
          company={company}
          color={primaryColor}
        />
      );

    case 'modern':
      return (
        <QuoteTemplateModern
          quoteData={quoteData}
          onClose={onClose}
          template={template || undefined}
          company={company}
          color={primaryColor}
        />
      );

    case 'corporate':
      return (
        <QuoteTemplateCorporate
          quoteData={quoteData}
          onClose={onClose}
          template={template || undefined}
          company={company}
          color={primaryColor}
        />
      );

    case 'elegant':
      return (
        <QuoteTemplateElegant
          quoteData={quoteData}
          onClose={onClose}
          template={template || undefined}
          company={company}
          color={primaryColor}
        />
      );

    default:
      // Fallback vers template classique pour tout template inconnu
      logger.warn(`Template inconnu: ${templateName}, fallback vers template classique`);
      return (
        <QuoteTemplateClassic
          quoteData={quoteData}
          onClose={onClose}
          template={template || undefined}
          company={company}
          color={primaryColor}
        />
      );
  }
};
